import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  getCommercialLoanById,
  getCommercialODLoanStatementByLoanId,
  getCommercialODTransactionStatementByLoanId,
  getCommercialOdWriteOffCollectionsByLoanId,
  writeOffCommercialOdLoanByLoanId,
} from "../../../actions/loan.action";
import { showNotifier } from "../../../actions/ui.action";
import Button from "../../../components/button";
import { ConfirmDialogType } from "../../../components/confirm-dialog";
import Modal from "../../../components/modal";
import {
  CommercialLoanDetails,
  CommercialODLoanStatement,
  CommercialODTransactionStatement,
} from "../../../dto";
import { ReactComponent as LoaderIcon } from "../../../images/loader.svg";
import dispatch from "../../../middleware";
import { NotifierBgColor } from "../../../models";
import { ReduxState } from "../../../reducers";
import { dateToYYYYMMDD, isLoadingActive } from "../../../utils";
import { assertWriteOffFieldsValid } from "../../personal-loan-details/helper";
import WrittenOffDetails from "../../personal-loan-details/loan-details/writtenOffDetails";
import LoanTags from "../../personal-loan-details/loanTags";
import {
  WriteOffCollectionAmount,
  WriteOffLoanFields,
} from "../../personal-loan-details/types";
import LoanDetails from "./commercialLoanDetails";
import LoanStatement from "./commercialLoanStatement";
import LoanSummary from "./commercialLoanSummary";
import CommercialODInvoice from "./commercialOdInvoice";
import TransactionStatement from "./commercialTransactionStatement";

export default function CommercialODLoan() {
  const { loanId }: { loanId: string } = useParams();
  const storeDispatch = useDispatch();
  const [isAddInvoiceModalActive, setIsAddInvoiceModalActive] = useState(false);

  const loadingQueue = useSelector(
    (state: ReduxState) => state.loan.loadingQueue
  );
  const commercialLoanDetails: CommercialLoanDetails | null = useSelector(
    (state: ReduxState) => state.loan.singleCommercialLoan
  );
  const loanStatementDetails: CommercialODLoanStatement[] | null = useSelector(
    (state: ReduxState) => state.loan.commercialODLoanStatement
  );
  const transactionStatements: CommercialODTransactionStatement[] | null =
    useSelector(
      (state: ReduxState) => state.loan.commercialODTransactionStatement
    );

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [writeOffCollectionAmount, setWriteOffCollectionAmount] =
    useState<WriteOffCollectionAmount | null>(null);

  const fetchLoanDetails = useCallback(
    async (loanIdToBeFetched: string) => {
      setIsLoading(true);
      try {
        await dispatch(storeDispatch, getCommercialLoanById(loanIdToBeFetched));
      } catch (error) {
        setErrorMessage(error?.message);
      } finally {
        setIsLoading(false);
      }
    },
    [storeDispatch]
  );

  const fetchLoanStatement = useCallback(
    async (loanIdToBeFetched: string, asOf: Date) => {
      setIsLoading(true);
      try {
        if (!loanIdToBeFetched) return;
        await dispatch(
          storeDispatch,
          getCommercialODLoanStatementByLoanId(
            loanIdToBeFetched,
            dateToYYYYMMDD(asOf)
          )
        );
      } catch (error) {
        setErrorMessage(error?.message);
      } finally {
        setIsLoading(false);
      }
    },
    [storeDispatch]
  );

  const fetchTransactionStatement = useCallback(
    async (loanIdToBeFetched: string) => {
      setIsLoading(true);
      try {
        if (!loanIdToBeFetched) return;
        await dispatch(
          storeDispatch,
          getCommercialODTransactionStatementByLoanId(loanIdToBeFetched)
        );
      } catch (error) {
        setErrorMessage(error?.message);
      } finally {
        setIsLoading(false);
      }
    },
    [storeDispatch]
  );

  async function writeOffCommercialOdLoan(
    loanId: string,
    body: WriteOffLoanFields
  ) {
    try {
      const response: CommercialLoanDetails | null = await dispatch(
        storeDispatch,
        writeOffCommercialOdLoanByLoanId(loanId, body)
      );

      if (response) {
        fetchLoanDetails(loanId);
      }
    } catch (error) {
      showNotifier(
        NotifierBgColor.ERROR,
        error?.message || "An unknown error occurred"
      );
    }
  }

  async function handleWriteOffBtnClick(
    type: ConfirmDialogType,
    fieldData: WriteOffLoanFields
  ) {
    try {
      const { writeOffDate, reason } = fieldData;
      assertWriteOffFieldsValid(fieldData);

      const formattedDate = new Date(writeOffDate).toISOString();
      await writeOffCommercialOdLoan(loanId, {
        writeOffDate: formattedDate,
        reason,
      });
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : "Something went wrong";
      showNotifier(errorMsg, "error");
    }
  }

  const fetchWriteOffCollectionAmount = useCallback(
    async (loanId: string) => {
      try {
        const response: WriteOffCollectionAmount = await dispatch(
          storeDispatch,
          getCommercialOdWriteOffCollectionsByLoanId(loanId)
        );
        if (!response) return;

        setWriteOffCollectionAmount(response);
      } catch (error) {
        showNotifier(
          NotifierBgColor.ERROR,
          error?.message || "An unknown error occurred"
        );
      }
    },
    [storeDispatch]
  );

  useEffect(() => {
    fetchLoanDetails(loanId);
    fetchLoanStatement(loanId, new Date());
    fetchTransactionStatement(loanId);
    fetchWriteOffCollectionAmount(loanId);
  }, [
    fetchLoanDetails,
    fetchLoanStatement,
    fetchTransactionStatement,
    fetchWriteOffCollectionAmount,
    loanId,
  ]);

  if (errorMessage) {
    return <h4 className="text-center">{errorMessage}</h4>;
  }

  return (
    <>
      <div className="d-flex mb-4 justify-content-end">
        <Button
          onClick={() => setIsAddInvoiceModalActive(true)}
          title="Add Invoice"
          size="md"
        >
          Add Invoice Entry
        </Button>
      </div>
      <div className="dashboard-container">
        {isLoading ? (
          <LoaderIcon />
        ) : (
          <>
            {commercialLoanDetails && (
              <>
                <LoanTags
                  loanDetails={commercialLoanDetails}
                  loanType="COMMERCIAL_OD"
                />
                <LoanDetails commercialLoanDetails={commercialLoanDetails} />
                <LoanSummary
                  commercialLoanDetails={commercialLoanDetails}
                  onWriteOffBtnClick={handleWriteOffBtnClick}
                />
              </>
            )}
          </>
        )}
      </div>

      {commercialLoanDetails?.writeOffMetadata && (
        <div className="dashboard-container mt-4">
          <WrittenOffDetails
            data={commercialLoanDetails.writeOffMetadata}
            writeOffCollectionAmount={writeOffCollectionAmount}
          />
        </div>
      )}

      <div className="dashboard-container mt-4">
        <TransactionStatement
          onSuccess={() =>
            dispatch(
              storeDispatch,
              getCommercialODTransactionStatementByLoanId(loanId)
            )
          }
          transactionStatements={transactionStatements}
          isLoanLoading={isLoadingActive(loadingQueue)}
          loanId={loanId}
        />
      </div>

      <div className="dashboard-container mt-4">
        <LoanStatement
          loanStatementDetails={loanStatementDetails}
          isLoanLoading={isLoadingActive(loadingQueue)}
          loanId={loanId}
        />
      </div>
      <Modal
        open={isAddInvoiceModalActive}
        title="Add Invoice Entry"
        size="large"
        hasSubmitButton={false}
        hasCancelButton={false}
        onClose={() => setIsAddInvoiceModalActive(false)}
      >
        <CommercialODInvoice loanId={loanId} />
      </Modal>
    </>
  );
}
